import React from 'react';
import { Check } from 'lucide-react';
import { StepIndicatorProps } from './types';
import newStepIcon from '@/assets/images/new-icon.svg';
import newStepPrimaryIcon from '@/assets/images/new-icon-primary.svg';

const steps = [
  { number: 1, title: 'Basic Details' },
  { number: 2, title: 'Body Metrics and Goals' },
  { number: 3, title: 'Finish' },
] as const;

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep }) => {
  const clampedStep = Math.min(Math.max(currentStep, 1), steps.length);
  const activeStep = steps[clampedStep - 1];

  return (
    <>
      <div
        className="rounded-2xl border border-border bg-muted/20 px-4 py-3.5 md:hidden"
        role="progressbar"
        aria-valuenow={clampedStep}
        aria-valuemin={1}
        aria-valuemax={steps.length}
        aria-label={`Step ${clampedStep} of ${steps.length}: ${activeStep.title}`}
      >
        <div className="flex items-center justify-center">
          {steps.map((step, index) => {
            const isCompleted = clampedStep > step.number;
            const isActive = clampedStep === step.number;

            return (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold transition-all ${
                      isCompleted
                        ? 'bg-cs-primary text-white'
                        : isActive
                          ? 'bg-cs-primary text-white ring-4 ring-cs-primary/15'
                          : 'border border-muted-foreground/25 bg-background text-muted-foreground'
                    }`}
                  >
                    {isCompleted ? <Check className="h-4 w-4" strokeWidth={2.5} /> : step.number}
                  </div>
                </div>
                {index < steps.length - 1 ? (
                  <div
                    className={`mx-2 mb-0 h-0.5 w-10 rounded-full sm:w-14 ${
                      clampedStep > step.number ? 'bg-cs-primary' : 'bg-muted-foreground/20'
                    }`}
                  />
                ) : null}
              </React.Fragment>
            );
          })}
        </div>

        <div className="mt-3 text-center">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Step {clampedStep} of {steps.length}
          </p>
          <p className="mt-1 text-sm font-semibold leading-snug text-cs-primary">{activeStep.title}</p>
        </div>
      </div>

      <div className="hidden w-full items-center md:flex">
        {steps.map((step, index) => {
          const isReachedStep = clampedStep >= step.number;

          return (
            <React.Fragment key={step.number}>
              <div className="flex items-center gap-2">
                <img
                  src={isReachedStep ? newStepPrimaryIcon : newStepIcon}
                  alt=""
                  aria-hidden="true"
                  className={`h-4 w-4 shrink-0 object-contain ${isReachedStep ? '' : 'opacity-60'}`}
                />
                <span
                  className={`whitespace-nowrap text-sm font-medium lg:text-base ${
                    isReachedStep ? 'text-cs-primary' : 'text-muted-foreground'
                  }`}
                >
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className="mx-3 flex-1">
                  <div
                    className={`h-px transition-colors ${
                      clampedStep > step.number ? 'bg-cs-primary/70' : 'bg-muted-foreground/20'
                    }`}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

export default StepIndicator;
